<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'ChallengeManagement'})"/>
                {{ $t('health.challenge.newTemplate') }}
              </h1>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <form
          class="margin-bottom-xl"
          v-on:submit.prevent
          v-on:keypress.enter.prevent="onSave">
          <grid-row column-align="center" col-spaced>
            <grid-col col-size="1">
              <label-input
                ref="nameInput"
                form-size="m"
                :margin="false"
                :label="$t('health.challenge.name')"
                v-model="name"
                :type="'text'"
                :error="errorName"
                :errorMessage="$t('commons.mandatoryFieldMessage')"
              />
            </grid-col>
            <grid-col col-size="1">
              <label-input
                ref="nameEngInput"
                form-size="m"
                :margin="false"
                :label="$t('health.challenge.nameEng')"
                v-model="nameEng"
                :type="'text'"
                :error="errorNameEng"
                :errorMessage="$t('commons.mandatoryFieldMessage')"
              />
            </grid-col>
            <grid-col col-size="1">
              <label-input
                type="Number"
                ref="duration"
                form-size="m"
                :label="$t('health.challenge.daysOfDuration')"
                :margin="false"
                :value="duration.toString()"
                v-on:input="(value) => {this.duration = value}"
                :error="errorDuration"
                :errorMessage="durationErrorMessage"
                required>
              </label-input>
            </grid-col>
            <grid-col col-size="1"/>
          </grid-row>
          <grid-row column-align="center" col-spaced>
            <grid-col col-size="2">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                  ref="descriptionInput"
                  form-size="m"
                  :margin="false"
                  :label="$t('health.challenge.description')"
                  v-model="description"
                  :type="'text'"
                  :error="errorDescription"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                >
                </label-input>
              </div>
            </grid-col>
            <grid-col col-size="2">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                  ref="descriptionEngInput"
                  form-size="m"
                  :margin="false"
                  :label="$t('health.challenge.descriptionEng')"
                  v-model="descriptionEng"
                  :type="'text'"
                  :error="errorDescriptionEng"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                >
                </label-input>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center" col-spaced>
            <grid-col col-size="2">
              <p>{{ getNotificationMessage }}</p>
            </grid-col>
            <grid-col col-size="2">
              <p>{{ getNotificationMessageEng }}</p>
            </grid-col>
          </grid-row>
          <div v-if="isAccumulatedMode">
            <grid-row column-align="center" col-spaced>
              <grid-col col-size="2">
                <div class="variable-input-field variable-input-field-name">
                  <label-input
                    ref="resultDescriptionInput"
                    form-size="m"
                    :margin="false"
                    :label="$t('health.challenge.resultDescription')"
                    v-model="resultDescription"
                    :type="'text'"
                    :error="errorResultDescription"
                    :errorMessage="$t('commons.mandatoryFieldMessage')"
                  >
                  </label-input>
                </div>
                <p>{{ this.$t('health.challenge.resultDescriptionFormat') }}</p>
              </grid-col>
              <grid-col col-size="2">
                <div class="variable-input-field variable-input-field-name">
                  <label-input
                    ref="resultDescriptionEngInput"
                    form-size="m"
                    :margin="false"
                    :label="$t('health.challenge.resultDescriptionEng')"
                    v-model="resultDescriptionEng"
                    :type="'text'"
                    :error="errorResultDescriptionEng"
                    :errorMessage="$t('commons.mandatoryFieldMessage')"
                  >
                  </label-input>
                </div>
              </grid-col>
            </grid-row>
            <grid-row column-align="center" col-spaced>
              <grid-col col-size="2">
                <p>{{ getNotificationMessageResult }}</p>
              </grid-col>
              <grid-col col-size="2">
                <p>{{ getNotificationMessageResultEng }}</p>
              </grid-col>
            </grid-row>
          </div>
          <grid-row column-align="center">
            <grid-col col-size="2">
              <h1>{{ this.$t('health.challenge.type') }}</h1>
              <radio
                v-for="(mode, index) in getChallengeModes"
                :key="index"
                v-on:change="onModeSelected(mode)"
                :checked="isModeChecked(mode)"
                radio-size="s"
                :id="String(mode.id)"
                :label="mode.label"/>
            </grid-col>
            <grid-col col-size="2">
              <h1>{{ this.$t('health.challenge.category') }}</h1>
              <vue-single-select
                :placeholder="''"
                class="selection-left"
                v-model="category"
                :option-label="'label'"
                :options="getCategories"
              />
            </grid-col>
          </grid-row>
          <grid-row column-align="center" col-spaced>
            <grid-col col-size="2">
              <h1>{{ this.$t('health.challenge.formula') }}</h1>
              <challenge-formula-form
                :variableReferences="variableEndings"
                :mode="getMode"
                v-model="formulaData"
              />
            </grid-col>
            <grid-col col-size="2">
              <h1>{{ this.$t('health.challenge.levels') }}</h1>
              <challenge-levels-form
                :mode="getMode"
                v-model="levelsData"
              />
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="variable-input-field submit-buttons align-right">
                <btn-pill
                  :btnText="$t('commons.save')"
                  v-on:click="onSave"
                  btn-size="m">
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
        </form>
      </div>
    </main-layout>
  </div>
</template>

<script>

import MainLayout from '@/layouts/MainLayout.vue';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Alert from '@/components/shared/alert/Alert';
import ChallengeFormulaForm from 'BRAND/components/challenge/ChallengeFormulaForm';
import ChallengeLevelsForm from 'BRAND/components/challenge/ChallengeLevelsForm';
import variableReferenceApi from 'BRAND/api/variableReference';
import Radio from '@/components/shared/forms/Radio';
import VueSingleSelect from 'vue-single-select';
import api from 'BRAND/api/challenge';

export default {
  name: 'AddChallengeTemplate',
  components: {
    MainLayout,
    GridCol,
    GridRow,
    Chevron,
    LabelInput,
    BtnPill,
    Alert,
    ChallengeFormulaForm,
    ChallengeLevelsForm,
    Radio,
    VueSingleSelect
  },
  data () {
    return {
      variableEndings: [],
      /* Form data */
      name: '',
      nameEng: '',
      description: '',
      descriptionEng: '',
      resultDescription: '',
      resultDescriptionEng: '',
      category: null,
      duration: 30,
      formulaData: {},
      levelsData: [],
      mode: {},
      /* Alerts */
      alertVisible: false,
      alertMessage: '',
      alertType: '',
      /* Error in fields */
      errorName: false,
      errorNameEng: false,
      errorDescription: false,
      errorDescriptionEng: false,
      errorResultDescription: false,
      errorResultDescriptionEng: false,
      errorDuration: false,
      durationErrorMessage: ''
    };
  },
  created () {
    this.requestData();
  },
  mounted () {
    this.mode = this.getChallengeModes[0];
  },
  computed: {
    getBreadcrumb () {
      const self = this.$t('health.challenge.newTemplate');
      return [
        {name: this.$t('menu.workspace'), link: '/workspace'},
        {name: this.$t('health.menu.challengeManagement'), link: '/challenge-management'},
        {name: self, link: '/add-challenge-template', disabled: true}
      ];
    },
    getChallengeModes () {
      return [
        {id: 0, label: this.$t('health.challenge.types.accumulatedLabel'), alias: 'accumulated'},
        // {id: 1, label: this.$t('health.challenge.types.streakLabel'), alias: 'streak'},
        {id: 2, label: this.$t('health.challenge.types.days_countLabel'), alias: 'days_count'}
      ];
    },
    getCategories () {
      return [
        {
          label: this.$t('health.challenge.categories.glucose'),
          value: 'glucose'
        },
        {
          label: this.$t('health.challenge.categories.sport'),
          value: 'sport'
        },
        {
          label: this.$t('health.challenge.categories.nutrition'),
          value: 'nutrition'
        },
        {
          label: this.$t('health.challenge.categories.rest'),
          value: 'rest'
        }, {
          label: this.$t('health.challenge.categories.vibeBattery'),
          value: 'vibeBattery'
        }
      ];
    },
    getMode () {
      return this.mode;
    },
    isAccumulatedMode () {
      return this.mode.alias === 'accumulated';
    },
    getNotificationMessage () {
      return '¡Ya está en marcha el reto ' + this.name + '🏆 Desde el {fecha_de_inicio} hasta el {fecha_de_fin} tendrás que ' + this.description + ' para completarlo. ¡A por ello!🚀.';
    },
    getNotificationMessageEng () {
      return 'The ' + this.nameEng + ' challenge is up and running!🏆 From {start_date} to {end_date} you will have to ' + this.descriptionEng + ' to complete it. Go for it!🚀.';
    },
    getNotificationMessageResult () {
      return '¡Tiempo!⏱️🖐️ El reto ' + this.name + ' ha finalizado 🏁. ' + this.resultDescription.replace('%s', '50') + '.';
    },
    getNotificationMessageResultEng () {
      return 'Time up! ⏱️🖐️ The ' + this.nameEng + ' challenge is over 🏁. ' + this.resultDescriptionEng.replace('%s', '50') + '.';
    }
  },
  methods: {
    onModeSelected (mode) {
      this.mode = mode;
      this.resultDescription = '';
      this.resultDescriptionEng = '';
    },
    isModeChecked (mode) {
      return this.mode.id === mode.id ? 'checked' : '';
    },
    requestData () {
      variableReferenceApi.getVariableReferences(this).then(data => {
        this.variableEndings = data;
      }).catch(() => {
        showAlert(this, this.$t('health.challenge.errorFetchingVariableReferences'));
      });
    },
    onSave () {
      clearErrors(this);
      if (isValidForm(this)) {
        const variables = this.formulaData.variables.map(variable => {
          const result = {
            symbolOp: variable.model.symbolOp,
            variable: variable.model.variable,
            constantValue: variable.model.constantValue
          };
          if (variable.model.op != null) {
            result.op = variable.model.op.id;
          }
          return result;
        });
        const comparationVariable = {
          symbolOp: this.formulaData.comparationVariable.model.symbolOp,
          variable: this.formulaData.comparationVariable.model.variable,
          constantValue: this.formulaData.comparationVariable.model.constantValue
        };
        if (this.formulaData.comparationVariable.model.op != null) {
          comparationVariable.op = this.formulaData.comparationVariable.model.op.id;
        }
        const formula = {
          variables,
          comparationVariable
        };
        const body = {
          'name': this.name,
          'nameEng': this.nameEng,
          'description': this.description,
          'descriptionEng': this.descriptionEng,
          'resultDescription': this.resultDescription,
          'resultDescriptionEng': this.resultDescriptionEng,
          'duration': this.duration,
          'type': this.mode.alias,
          'formula': formula,
          'levels': this.levelsData,
          'category': this.category.value
        };

        api.createTemplate(this, body).then(() => {
          this.$router.back();
        }).catch(error => {
          showAlert(this, this.$t('health.challenge.errorTemplateCreate') + ': ' + error.body.message, 'error');
        });
      } else {
        if (this.alertVisible) {
          showAlert(this, this.alertMessage, 'error');
        }
      }
    }
  }
};

const clearErrors = (context) => {
  context.alertVisible = false;
  context.alertMessage = '';
  context.alertType = '';
  context.errorName = false;
  context.errorNameEng = false;
  context.errorDescription = false;
  context.errorDescriptionEng = false;
  context.errorResultDescription = false;
  context.errorResultDescriptionEng = false;
};

const isAccumulatedMode = (mode) => {
  return mode.alias === 'accumulated';
};

const isValidForm = (context) => {
  let valid = true;
  if (!(context.name && context.name !== '')) {
    valid = false;
    context.errorName = true;
  }
  if (!(context.nameEng && context.nameEng !== '')) {
    valid = false;
    context.errorNameEng = true;
  }
  if (context.duration == null || context.duration === '') {
    valid = false;
    context.errorDuration = true;
    context.durationErrorMessage = context.$t('commons.mandatoryFieldMessage');
  }

  if (!(context.description && context.description !== '')) {
    valid = false;
    context.errorDescription = true;
  }

  if (!(context.descriptionEng && context.descriptionEng !== '')) {
    valid = false;
    context.errorDescriptionEng = true;
  }
  const isAccumulated = isAccumulatedMode(context.mode);
  if (isAccumulated) {
    if (!(context.resultDescription && context.resultDescription !== '')) {
      valid = false;
      context.errorResultDescription = true;
    }

    if (!(context.resultDescriptionEng && context.resultDescriptionEng !== '')) {
      valid = false;
      context.errorResultDescriptionEng = true;
    }
  }

  if (context.category == null) {
    context.alertVisible = true;
    context.alertMessage = context.$t('health.challenge.categoryNotSelectedError');
    context.alertType = 'error';
    valid = false;
  }

  if (context.formulaData == null) {
    valid = false;
  }
  const comparationData = context.formulaData.comparationVariable;
  if (comparationData == null || !isValidComparationVariable(comparationData, isAccumulated)) {
    context.alertVisible = true;
    context.alertMessage = context.$t('health.challenge.incompletecomparationVariableDataError');
    context.alertType = 'error';
    valid = false;
  }

  const variables = context.formulaData.variables;
  if (variables == null || variables.length < 1) {
    valid = false;
    context.alertVisible = true;
    context.alertMessage = context.$t('health.challenge.noVariablesDataError');
    context.alertType = 'error';
    valid = false;
  } else {
    variables.forEach((variable, index) => {
      if (!isValidVariable(variable, index)) {
        valid = false;
        context.alertVisible = true;
        context.alertMessage = context.$t('health.challenge.incompleteVariableDataError');
        context.alertType = 'error';
      }
    });
  }

  const levels = context.levelsData;
  if (levels == null || levels.length < 1) {
    valid = false;
    context.alertVisible = true;
    context.alertMessage = context.$t('health.challenge.noLevelsError');
    context.alertType = 'error';
    valid = false;
  } else {
    let validlevels = true;
    levels.forEach(level => {
      if (!isValidLevel(level)) {
        valid = false;
        validlevels = false;
        context.alertVisible = true;
        context.alertMessage = context.$t('health.challenge.incompleteLevelDataError');
        context.alertType = 'error';
      }
    });
    if (validlevels && !isAccumulated) {
      const longestStreakValue = Math.max(...levels.map(o => o.value));
      const shortestStreakValue = Math.min(...levels.map(o => o.value));
      if (shortestStreakValue <= 0) {
        valid = false;
        context.alertVisible = true;
        context.alertMessage = context.$t('health.challenge.levelValueLowerThanZeroError');
        context.alertType = 'error';
      } else if (longestStreakValue > context.duration) {
        valid = false;
        context.alertVisible = true;
        context.alertMessage = context.$t('health.challenge.maxLevelValueHigherThanDurationError');
        context.alertType = 'error';
      }
    }
    if (validlevels) {
      const sortedLevels = levels.sort(function (a, b) {
        return a.order - b.order;
      });
      if (isAccumulated) {
        if (!areLevelValuesValidAscending(sortedLevels) && !areLevelValuesValidDescending(sortedLevels)) {
          valid = false;
          context.alertVisible = true;
          context.alertMessage = context.$t('health.challenge.levelValuesAreNotConsistentlyAscendingOrDescending');
          context.alertType = 'error';
        }
      } else if (!areLevelValuesValidAscending(sortedLevels)) {
        valid = false;
        context.alertVisible = true;
        context.alertMessage = context.$t('health.challenge.levelValuesAreNotAscending');
        context.alertType = 'error';
      }
    }
  }

  return valid;
};

/**
 * Valid increasing
 * 0 - 100
 * 1 - 50
 * 2 - 20
 *
 * Valid decreasing
 * 0 - 20
 * 1 - 50
 * 2 - 100
 *
 * @param levels ordered ascendently
 * @param onlyIncreasing levels will only be valid if they are increasing
 */
const areLevelValuesValidAscending = (levels) => {
  let validIncreasing = true;
  for (let i = 1; i < levels.length; i++) {
    const previousLevel = levels[i - 1];
    const currentLevel = levels[i];
    if (parseInt(previousLevel.value) <= parseInt(currentLevel.value)) {
      validIncreasing = false;
    }
  }
  return validIncreasing;
};

const areLevelValuesValidDescending = (levels) => {
  let validDecreasing = true;
  for (let i = 1; i < levels.length; i++) {
    const previousLevel = levels[i - 1];
    const currentLevel = levels[i];
    if (parseInt(previousLevel.value) >= parseInt(currentLevel.value)) {
      validDecreasing = false;
    }
  }
  return validDecreasing;
};

const isValidLevel = (level) => {
  if (level == null) {
    return false;
  }
  if (level.order == null || level.order === '' || isNaN(level.order)) {
    return false;
  }
  if (level.value == null || level.value === '' || isNaN(level.value)) {
    return false;
  }
  return true;
};

const isValidVariable = (variable, index) => {
  if (variable.model == null) {
    return false;
  }
  const model = variable.model;
  if (index > 0 && (model.symbolOp == null || model.symbolOp === '')) {
    return false;
  }
  if (variable.isConstant) {
    if (model.constantValue == null || isNaN(model.constantValue)) {
      return false;
    }
  } else {
    if (model.variable == null || model.variable === '') {
      return false;
    }
    if (model.op == null || model.op.id === '') {
      return false;
    }
  }
  return true;
};

const isValidComparationVariable = (variable, isAccumulated) => {
  if (variable.model == null) {
    return false;
  }
  const model = variable.model;
  if (model.symbolOp == null || model.symbolOp === '') {
    return false;
  }
  if (isAccumulated) {
    return true;
  }
  let validConstant = true;
  let validVariable = true;
  if (model.constantValue == null || isNaN(model.constantValue)) {
    validConstant = false;
  }
  if (model.variable == null || model.variable === '') {
    validVariable = false;
  }
  if (model.op == null || model.op.id === '') {
    validVariable = false;
  }
  return validConstant || validVariable;
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 3000);
};

</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/challenge/AddChallengeTemplate.vue